import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip } from 'recharts'
import { Plus, Trash2, HeartPulse, Activity, Droplet } from 'lucide-react'
import { Card, CardContent } from '../../../components/ui/card'
import { Button } from '../../../components/ui/button'
import { useVitals } from '../../../hooks/useVitals'
import { useTheme } from '../../../contexts/ThemeContext'
import { useConfirm } from '../../../contexts/ConfirmDialogContext'
import { VitalsForm } from './VitalsForm'

const KIND_META = {
  blood_pressure: { Icon: HeartPulse, titleKey: 'bp.title', unitKey: 'bp.unit' },
  heart_rate:     { Icon: Activity,   titleKey: 'hr.title', unitKey: 'hr.bpm' },
  glucose:        { Icon: Droplet,    titleKey: 'glucose.title', unitKey: 'glucose.unit' },
}

function primaryValue(entry, kind) {
  if (kind === 'blood_pressure') return entry.systolic
  if (kind === 'heart_rate') return entry.bpm
  if (kind === 'glucose') return entry.glucose_mg_dl != null ? Number(entry.glucose_mg_dl) : null
  return null
}

function formatLatest(entry, kind) {
  if (!entry) return null
  if (kind === 'blood_pressure') return `${entry.systolic}/${entry.diastolic}`
  if (kind === 'heart_rate') return `${entry.bpm}`
  if (kind === 'glucose') return Number(entry.glucose_mg_dl).toFixed(1)
  return null
}

function formatShortDate(iso) {
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  } catch {
    return ''
  }
}

export function VitalsPanel({ kind }) {
  const { t } = useTranslation()
  const { primaryHsl } = useTheme()
  const primary500 = primaryHsl(500)
  const vitals = useVitals({ kind, days: 30 })
  const confirm = useConfirm()
  const [adding, setAdding] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [selectedId, setSelectedId] = useState(null)
  const meta = KIND_META[kind]
  const Icon = meta.Icon

  // Sparkline points in chronological order (left → right). Recharts
  // wants the oldest at index 0 even though the hook returns descending.
  const chartData = useMemo(() => {
    return [...vitals.entries]
      .reverse()
      .map((e) => ({
        id: e.id,
        date: formatShortDate(e.measured_at),
        value: primaryValue(e, kind),
        context: e.context,
        notes: e.notes,
      }))
      .filter((p) => typeof p.value === 'number' && Number.isFinite(p.value))
  }, [vitals.entries, kind])

  const handleSave = async (payload) => {
    setSubmitting(true)
    await vitals.create(payload)
    setSubmitting(false)
    setAdding(false)
  }

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: t('healthProfile.vitals.confirmDelete'),
      confirmLabel: t('common.delete'),
      variant: 'destructive',
      icon: Trash2,
    })
    if (ok) {
      await vitals.remove(id)
      setSelectedId(null)
    }
  }

  const selected = selectedId ? vitals.entries.find((e) => e.id === selectedId) : null
  const latestValue = formatLatest(vitals.latest, kind)
  const unit = t(`healthProfile.vitals.${meta.unitKey}`)
  const title = t(`healthProfile.vitals.${meta.titleKey}`)

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null
    const p = payload[0].payload
    return (
      <div className="bg-gray-900 dark:bg-gray-800 text-white px-3 py-2 rounded-lg shadow-lg border border-app-primary-500/30 text-xs">
        <p className="text-muted-foreground">{p.date}</p>
        <p className="font-mono font-bold text-app-primary-400">
          {p.value} <span className="font-normal text-muted-foreground">{unit}</span>
        </p>
        {p.context && (
          <p className="text-[10px] text-muted-foreground mt-0.5" dir="auto">
            {t(`healthProfile.vitals.context.${p.context}`)}
          </p>
        )}
        {p.notes && (
          <p className="text-[10px] text-muted-foreground mt-0.5 max-w-[160px] truncate" dir="auto">
            {p.notes}
          </p>
        )}
      </div>
    )
  }

  return (
    <Card>
      <CardContent className="p-3 sm:p-4 space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-1.5" dir="auto">
            <Icon className="w-4 h-4 text-app-primary-500" />
            {title}
          </h3>
          {!adding && (
            <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
              <Plus className="w-4 h-4 me-1" />
              {t('healthProfile.vitals.addReading')}
            </Button>
          )}
        </div>

        {adding && (
          <VitalsForm
            kind={kind}
            onSave={handleSave}
            onCancel={() => setAdding(false)}
            submitting={submitting}
          />
        )}

        {!adding && (vitals.latest ? (
          <>
            <div className="flex items-baseline gap-2" dir="ltr">
              <span className="text-2xl font-bold font-mono text-app-primary-500">{latestValue}</span>
              <span className="text-xs text-muted-foreground">{unit}</span>
              {vitals.latest.context && (
                <span className="text-[11px] text-muted-foreground ms-2" dir="auto">
                  · {t(`healthProfile.vitals.context.${vitals.latest.context}`)}
                </span>
              )}
            </div>
            {chartData.length >= 2 && (
              <div dir="ltr" className="w-full">
                <ResponsiveContainer width="100%" height={100}>
                  <AreaChart
                    data={chartData}
                    margin={{ top: 5, right: 5, left: -20, bottom: 0 }}
                    onClick={(state) => {
                      const p = state?.activePayload?.[0]?.payload
                      if (p?.id) setSelectedId(p.id)
                    }}
                  >
                    <defs>
                      <linearGradient id={`vitalsGradient-${kind}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={primary500} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={primary500} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis
                      dataKey="date"
                      interval="preserveStartEnd"
                      tick={{ fontSize: 10, fontFamily: '"JetBrains Mono", monospace' }}
                      stroke="#6B7280"
                    />
                    <YAxis
                      domain={['dataMin - 5', 'dataMax + 5']}
                      tick={{ fontSize: 10, fontFamily: '"JetBrains Mono", monospace' }}
                      stroke="#6B7280"
                      width={28}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke={primary500}
                      strokeWidth={2}
                      fill={`url(#vitalsGradient-${kind})`}
                      dot={{ fill: primary500, strokeWidth: 0, r: 2 }}
                      activeDot={{ r: 4, fill: primary500, stroke: '#fff', strokeWidth: 2 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
            {selected && (
              <div className="text-xs text-muted-foreground p-2 rounded-md bg-muted/30 flex items-start gap-2" dir="auto">
                <div className="flex-1 min-w-0">
                  <p className="font-mono">
                    {formatShortDate(selected.measured_at)} · {primaryValue(selected, kind)} {unit}
                  </p>
                  {selected.context && (
                    <p>{t(`healthProfile.vitals.context.${selected.context}`)}</p>
                  )}
                  {selected.notes && (
                    <p className="mt-0.5 whitespace-pre-wrap">{selected.notes}</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleDelete(selected.id)}
                  aria-label={t('common.delete')}
                  className="p-1 text-muted-foreground hover:text-red-500 flex-shrink-0"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </>
        ) : (
          <p className="text-xs text-muted-foreground" dir="auto">
            {t('healthProfile.vitals.empty')}
          </p>
        ))}
      </CardContent>
    </Card>
  )
}
